/**
 * Mobile menu.
 *
 * A full-screen panel that slides in from the left and sits BEHIND the header
 * bar, so the logo, Close, search and cart stay visible on top of it.
 *
 * WHERE THE WORK IS SPLIT
 *
 *   CSS   — everything about how it looks and where it sits: the slide, the
 *           accordion height, the header's colour swap and its 1px rule, the
 *           stacking order. See the mobile menu comment block in
 *           sections/header.liquid and "Header state machine" in critical.css.
 *   here  — the state itself, and the four things state has to drag along with
 *           it: focus containment, scroll lock, the ARIA contract, and the
 *           inert attributes that keep off-screen content out of the tab order.
 *
 * ONE STATE ATTRIBUTE. `data-menu-open` goes on <site-header> and nowhere else.
 * The bar reads it through Tailwind's `group-data-menu-open:` and the panel
 * reads it the same way, so there is a single source of truth and no chance of
 * the bar and the panel disagreeing about whether the menu is open.
 *
 * WHY THIS IS A REAL ASSET FILE AND NOT A {% javascript %} BLOCK: same reason as
 * assets/site-header.js — section JS is bundled and injected by Shopify in an
 * order we cannot see or control, and it was measured NOT running at all on the
 * live store. Anything global goes in assets/ with an explicit `<script defer>`.
 *
 * LOAD ORDER: after site-header.js, which publishes `--header-height` (the
 * panel's top padding is derived from it). Nothing here depends on that script
 * having run — critical.css carries a measured fallback for the variable — but
 * keeping the order matches the dependency.
 */
if (!customElements.get('mobile-menu')) {
  customElements.define(
    'mobile-menu',
    class MobileMenu extends HTMLElement {
      connectedCallback() {
        /**
         * Looked up globally, not with closest(): this panel is its own section
         * now and is a SIBLING of the header's section inside the header group,
         * not a descendant of <site-header>. The trigger is still found by its
         * data attribute rather than its position, the same convention as
         * data-search-trigger and data-cart-trigger.
         *
         * NOT CACHED ON `this`, DELIBERATELY. The header is a different section
         * from this one, so the theme editor can re-render it on its own — and a
         * reference taken here would then point at a detached element while this
         * panel carried on writing `data-menu-open` to it, with the visible
         * result that the bar simply stops changing colour when the menu opens.
         * currentHeader() below re-queries at each use, the same way
         * assets/cart-drawer.js and assets/site-credits.js do. The local here
         * exists only to fail loudly below if the header is missing at upgrade.
         *
         * KNOWN, NOT FIXED: `this.trigger` has the same staleness problem — it
         * lives inside the header — but it also owns an addEventListener /
         * removeEventListener pair, which needs a stable reference to avoid
         * leaking a listener. Re-querying it correctly means moving to delegation,
         * which is a larger change than this one. The symptom if it ever bites is
         * narrower: `aria-expanded` announced on the wrong button.
         */
        var header = document.querySelector('site-header');
        this.trigger = document.querySelector('[data-menu-trigger]');

        /**
         * Fail loudly and leave the closed state alone. The panel keeps the
         * `inert` it was rendered with, so a broken wiring is an unreachable
         * menu rather than a screen-reader trap sitting off the left edge.
         */
        if (!header || !this.trigger) {
          console.warn('[mobile-menu] no <site-header> or [data-menu-trigger] found; menu disabled');
          return;
        }

        this.isOpen = false;

        /**
         * Only the elements WE made inert, so closing never clears an `inert`
         * that something else owns.
         */
        this.inerted = [];

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleBreakpoint = this.handleBreakpoint.bind(this);

        this.trigger.addEventListener('click', this.handleTriggerClick);
        this.addEventListener('click', this.handleClick);
        document.addEventListener('keydown', this.handleKeydown);

        /**
         * `64rem` is Tailwind's `lg`, the breakpoint at which the panel becomes
         * display:none. Rem in a media query resolves against the initial 16px
         * regardless of the root font-size, which is also how Tailwind's own
         * query resolves — so the two cannot drift apart.
         */
        this.desktopQuery = window.matchMedia('(min-width: 64rem)');
        this.desktopQuery.addEventListener('change', this.handleBreakpoint);
      }

      disconnectedCallback() {
        if (this.trigger) this.trigger.removeEventListener('click', this.handleTriggerClick);
        this.removeEventListener('click', this.handleClick);
        document.removeEventListener('keydown', this.handleKeydown);
        if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.handleBreakpoint);

        /**
         * A menu torn down while open (the theme editor re-rendering the header
         * is the realistic case) would otherwise leave the page scroll-locked
         * and half of it inert, with nothing left alive to undo either.
         */
        if (this.isOpen) {
          this.releaseScroll();
          this.setPageInert(false);
          var header = this.currentHeader();
          if (header) header.removeAttribute('data-menu-open');
          this.removeAttribute('data-open');
        }
      }

      /**
       * The live <site-header>, re-queried rather than cached — see the note in
       * connectedCallback. Every caller must handle null: the header section can
       * be absent mid-re-render in the theme editor.
       */
      currentHeader() {
        return document.querySelector('site-header');
      }

      /* ----------------------------------------------------------------- */
      /* Open / close                                                       */
      /* ----------------------------------------------------------------- */

      handleTriggerClick(event) {
        event.preventDefault();
        this.setOpen(!this.isOpen);
      }

      open() {
        this.setOpen(true);
      }

      close() {
        this.setOpen(false);
      }

      setOpen(next) {
        if (next === this.isOpen) return;

        /**
         * The header search panel sits at z-45, ABOVE this one's 40, so leaving it
         * open would put it squarely over the menu. Closed BEFORE setPageInert()
         * below runs, because that would otherwise make the search section inert
         * while it is still on screen — the same ordering rule cart-drawer.js
         * follows for this menu.
         */
        if (next) document.querySelector('search-overlay')?.close?.();

        this.isOpen = next;

        /**
         * Two attributes, each local to the element that reads it: the bar's
         * utilities key off `data-menu-open` on <site-header>, and this panel's
         * own slide and link stagger key off `data-open` on itself. They cannot
         * be one attribute any more, because the two elements are in different
         * sections and neither contains the other.
         */
        var header = this.currentHeader();
        if (header) header.toggleAttribute('data-menu-open', next);
        this.toggleAttribute('data-open', next);
        this.trigger.setAttribute('aria-expanded', String(next));

        /**
         * The panel is off-screen, not hidden, so this is what actually removes
         * it from the tab order and the accessibility tree when closed.
         */
        this.toggleAttribute('inert', !next);

        this.setPageInert(next);

        if (next) {
          this.lockScroll();
        } else {
          this.releaseScroll();
        }

        /**
         * No focus move on open: the trigger IS the close button — it stays put
         * and relabels — so focus is already in the right place and the tab
         * order runs Close, logo, search, cart, then the menu, in that order.
         */
      }

      /* ----------------------------------------------------------------- */
      /* Focus containment                                                  */
      /* ----------------------------------------------------------------- */

      /**
       * Make every top-level region of the page inert except the one containing
       * the header, which is also the one containing this panel.
       *
       * This is a focus trap without a focus trap: no keydown interception, no
       * "first and last focusable" bookkeeping that goes wrong the moment a
       * disabled button or a hidden input appears. The browser simply has
       * nowhere else to send focus, so Tab cycles the header and the menu and
       * then leaves for the browser chrome, which is correct.
       *
       * KNOWN LIMITATION: Shopify injects its own elements as children of
       * <body>, and while the menu is open those are inert too. Everything is
       * restored on close, and the theme editor is the only place it could
       * plausibly be noticed.
       */
      setPageInert(on) {
        if (!on) {
          this.inerted.forEach(function (element) {
            element.removeAttribute('inert');
          });
          this.inerted = [];
          return;
        }

        /**
         * TWO elements have to stay live, not one: the header's top-level
         * ancestor AND this panel's. They are the same element only when the
         * section group renders inside a single wrapper — and Shopify does not
         * guarantee that, so in the flat shape this panel is its own child of
         * <body>. Keeping only the header's would make the open menu inert and
         * leave nothing on the page clickable at all.
         */
        var header = this.currentHeader();
        var keep = [this.topLevelAncestor(this)];
        if (header) keep.push(this.topLevelAncestor(header));
        var self = this;

        Array.prototype.forEach.call(document.body.children, function (element) {
          if (keep.indexOf(element) !== -1) return;
          if (element.hasAttribute('inert')) return;

          element.setAttribute('inert', '');
          self.inerted.push(element);
        });
      }

      /**
       * Walk up to whichever child of <body> contains the given node — Shopify's
       * section wrapper, or the section-group wrapper if there is one. Used to
       * work out what must stay interactive while the menu is open.
       */
      topLevelAncestor(node) {
        while (node.parentElement && node.parentElement !== document.body) {
          node = node.parentElement;
        }
        return node;
      }

      /* ----------------------------------------------------------------- */
      /* Scroll lock                                                        */
      /* ----------------------------------------------------------------- */

      /**
       * Through the shared controller, per CLAUDE.md — it owns the single Lenis
       * instance and counts locks, so a cart drawer opening over this menu
       * cannot unlock the page underneath it on close.
       *
       * The direct fallback exists because the whole point of the controller is
       * that it might not be there: assets/scroll.js is deferred and can fail to
       * load. Without it the page would scroll behind an open full-screen menu.
       */
      lockScroll() {
        if (window.themeScroll) {
          this.lockedController = true;
          window.themeScroll.lock('mobile-menu');
          return;
        }

        this.lockedController = false;
        document.documentElement.classList.add('overflow-hidden');
      }

      releaseScroll() {
        if (this.lockedController && window.themeScroll) {
          window.themeScroll.unlock('mobile-menu');
        } else {
          document.documentElement.classList.remove('overflow-hidden');
        }
        this.lockedController = false;
      }

      /* ----------------------------------------------------------------- */
      /* Submenu accordions                                                 */
      /* ----------------------------------------------------------------- */

      handleClick(event) {
        if (!(event.target instanceof Element)) return;

        var trigger = event.target.closest('[data-accordion-trigger]');
        if (trigger && this.contains(trigger)) {
          this.toggleAccordion(trigger);
          return;
        }

        /**
         * Following a link normally unloads the page, so closing is invisible.
         * It matters for the case that does not: an in-page anchor, where the
         * menu would otherwise stay over the thing it just scrolled to.
         */
        var link = event.target.closest('a[href]');
        if (link && this.contains(link)) this.close();
      }

      toggleAccordion(trigger) {
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!panel) return;

        var willOpen = trigger.getAttribute('aria-expanded') !== 'true';

        // One at a time: close everything, then open the one that was asked for.
        this.closeAccordions();
        if (willOpen) this.setAccordion(trigger, panel, true);
      }

      closeAccordions() {
        var self = this;

        this.querySelectorAll('[data-accordion-trigger]').forEach(function (trigger) {
          var panel = document.getElementById(trigger.getAttribute('aria-controls'));
          if (panel) self.setAccordion(trigger, panel, false);
        });
      }

      /**
       * `data-open` drives the 0fr → 1fr height transition; `inert` is the part
       * that is easy to forget. A collapsed panel is `overflow: hidden` at zero
       * height, which hides its links visually but leaves every one of them
       * focusable — Tab would walk into a submenu that is not on screen.
       */
      setAccordion(trigger, panel, open) {
        trigger.setAttribute('aria-expanded', String(open));
        panel.toggleAttribute('data-open', open);
        panel.toggleAttribute('inert', !open);
      }

      /* ----------------------------------------------------------------- */
      /* Escape hatches                                                     */
      /* ----------------------------------------------------------------- */

      handleKeydown(event) {
        if (event.key !== 'Escape' || !this.isOpen) return;

        this.close();

        // Focus is already on the trigger unless the user tabbed away from it.
        this.trigger.focus();
      }

      /**
       * Above `lg` the panel is display:none. Left open across that boundary the
       * page would stay scroll-locked and inert behind a menu nobody can see —
       * a rotated phone or a resized desktop window is enough to hit it.
       */
      handleBreakpoint(event) {
        if (event.matches && this.isOpen) this.close();
      }
    }
  );
}
